import './rater.enterprise-fusion-score.scss';

import {
  Box,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ConvergeSectionTitle,
  NewFromSelectField,
} from '../../../../presentation';
import {
  EnterpriseRatingSeverity,
  EnterpriseRatingSeverityDefault,
  ModifierNoteLabel,
  NoteTargetAreaType,
} from '@converge/graphql';
import {
  FieldErrors,
  FieldValues,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { Fragment, ReactNode, useEffect, useMemo } from 'react';
import {
  FusionScoreModifierDisplay,
  NumberField,
  RaterNotesButton,
  RaterNotesRow,
} from '../common';
import {
  IUnderwritingEnterpriseRating,
  NoteTargetName,
  NoteTargetType,
} from '@converge/graphql';
import { RiskLevelType, toTitleCase } from '@converge/data';
import {
  useRaterActionDispatchContext,
  useRaterStateContextSelector,
} from '../data';

import { Stack } from '@mui/system';
import { compareBySeverity } from '@converge/utils';
import config from '@converge/config';
import { getParsedValue } from '../coverage-rating';
import { useTranslation } from 'react-i18next';

export const RISK_LEVEL_OPTIONS = Object.entries(EnterpriseRatingSeverity)
  .map((riskLevel) => {
    return {
      value: riskLevel[1],
      label: toTitleCase(riskLevel[1]),
    };
  })
  .sort(compareBySeverity);

interface ITableCellProps extends TableCellProps {
  children?: ReactNode;
}

const SCORE_MAPPINGS: Record<RiskLevelType, number> = {
  [RiskLevelType.VeryLow]: 10,
  [RiskLevelType.Low]: 9.3,
  [RiskLevelType.Moderate]: 8.5,
  [RiskLevelType.High]: 7.0,
  [RiskLevelType.VeryHigh]: 0,
};

const NEW_SCORE_MAPPINGS: Record<EnterpriseRatingSeverity, number> = {
  [EnterpriseRatingSeverity.VeryLow]: SCORE_MAPPINGS[RiskLevelType.VeryLow],
  [EnterpriseRatingSeverity.Low]: SCORE_MAPPINGS[RiskLevelType.Low],
  [EnterpriseRatingSeverity.Moderate]: SCORE_MAPPINGS[RiskLevelType.Moderate],
  [EnterpriseRatingSeverity.High]: SCORE_MAPPINGS[RiskLevelType.High],
  [EnterpriseRatingSeverity.VeryHigh]: SCORE_MAPPINGS[RiskLevelType.VeryHigh],
};

export const hasErrors = (
  fieldName: string,
  errors: FieldErrors<FieldValues>
) => {
  return errors && errors[fieldName] ? true : false;
};

export const RaterEnterpriseFusionScore = () => {
  const { t } = useTranslation();
  const {
    actions,
    actions: { setEnterpriseRatings, setQuoteField },
    dispatch,
  } = useRaterActionDispatchContext();

  const {
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();
  const {
    enterpriseRatings,
    erMaxFusionScoreModifier,
    erMinFusionScoreModifier,
    erRatedFusionScoreModifier,
    erSelectedFusionScoreModifier,
    erFusionScore,
    enterpriseRatingFusionScore,
    isEditable,
    hasErSelectedFusionScoreModifierChanged,
    uuid,
    inForcePolicy,
  } = useRaterStateContextSelector(
    ({
      quote: {
        enterpriseRatings,
        erMaxFusionScoreModifier,
        erMinFusionScoreModifier,
        erRatedFusionScoreModifier,
        erSelectedFusionScoreModifier,
        erFusionScore,
        hasErSelectedFusionScoreModifierChanged,
        uuid,
        inForcePolicy,
      },
      enterpriseRatingFusionScore,
      isEditable,
    }) => {
      return {
        enterpriseRatings,
        erMaxFusionScoreModifier,
        erMinFusionScoreModifier,
        erRatedFusionScoreModifier,
        erSelectedFusionScoreModifier,
        erFusionScore,
        enterpriseRatingFusionScore,
        isEditable,
        hasErSelectedFusionScoreModifierChanged,
        uuid,
        inForcePolicy,
      };
    }
  );

  const totalScore = useMemo(() => {
    return (
      erFusionScore ??
      enterpriseRatingFusionScore ??
      (enterpriseRatings ?? []).reduce(
        (acc, data) =>
          acc +
          NEW_SCORE_MAPPINGS[
            data.quote.riskLevel ?? EnterpriseRatingSeverityDefault
          ],
        0
      )
    );
  }, [erFusionScore, enterpriseRatingFusionScore, enterpriseRatings]);

  const methods = useForm();

  const handleRiskLevelChange =
    (uuid: string) => (option: EnterpriseRatingSeverity) => {
      const newEnterpriseScoreData = enterpriseRatings.map(
        (data): IUnderwritingEnterpriseRating => {
          if (data.uuid === uuid) {
            return {
              ...data,
              hasChanged: option !== data.quote.riskLevel,
              quote: {
                ...data.quote,
                riskLevel: option,
              },
            };
          }
          return data;
        }
      );
      dispatch(setEnterpriseRatings(newEnterpriseScoreData));
    };

  const hasInvalidSelectedFusionScoreModifier = hasErrors(
    'erSelectedFusionScoreModifier',
    errors
  );

  const hasInvalidData = hasInvalidSelectedFusionScoreModifier;

  useEffect(() => {
    dispatch(actions.setIsInvalidRaterData(hasInvalidData));
  }, [actions, dispatch, hasInvalidData]);

  const handleUpdateEnterpriseFusionScore = (value: number | null) => {
    const parsedValue = getParsedValue(value);

    if (
      parsedValue !== null &&
      parsedValue !== undefined &&
      parsedValue < (erMinFusionScoreModifier || 0)
    ) {
      setError(`erSelectedFusionScoreModifier`, {
        type: 'erSelectedFusionScoreModifier',
        message: `Fusion Score modifier must be a number between ${
          erMinFusionScoreModifier ?? 0
        } and ${erMaxFusionScoreModifier ?? 100}`,
      });
      return;
    }

    dispatch(setQuoteField('erSelectedFusionScoreModifier', value));
    clearErrors(`erSelectedFusionScoreModifier`);
  };
  return (
    <>
      <ConvergeSectionTitle mb={1}>
        Enterprise Fusion Score
      </ConvergeSectionTitle>
      <FormProvider {...methods}>
        <Table className="enterprise-clear-score-table">
          <TableHead>
            <TableRow>
              <RaterEnterpriseScoreTableHeaderCell width={'45%'}>
                Enterprise fusion score calculator
              </RaterEnterpriseScoreTableHeaderCell>
              <RaterEnterpriseScoreTableHeaderCell width={'35%'}>
                Risk level
              </RaterEnterpriseScoreTableHeaderCell>
              <RaterEnterpriseScoreTableHeaderCell width={'15%'}>
                Score
              </RaterEnterpriseScoreTableHeaderCell>
              <RaterEnterpriseScoreTableHeaderCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {enterpriseRatings.map((data, index) => {
              const {
                hasChanged,
                name,
                uuid,
                quote: { riskLevel, uuid: quoteUuid },
              } = data;
              return (
                <Fragment key={index}>
                  <TableRow
                    sx={{
                      backgroundColor: hasChanged
                        ? 'rgb(253, 245, 231, 0.75)'
                        : undefined,
                    }}
                  >
                    <RaterEnterpriseScoreTableBodyCell width={'25%'}>
                      {t(name ?? 'N/A')}
                    </RaterEnterpriseScoreTableBodyCell>
                    <RaterEnterpriseScoreTableBodyCell width={'25%'}>
                      <NewFromSelectField
                        disabled={!isEditable || inForcePolicy}
                        hideLabel={true}
                        name={uuid}
                        options={RISK_LEVEL_OPTIONS}
                        value={riskLevel ?? EnterpriseRatingSeverityDefault}
                        onChangeOption={(option) => {
                          handleRiskLevelChange(uuid)(
                            option
                              ? (option.value as EnterpriseRatingSeverity)
                              : EnterpriseRatingSeverityDefault
                          );
                        }}
                      />
                    </RaterEnterpriseScoreTableBodyCell>
                    <RaterEnterpriseScoreTableBodyCell width={'25%'}>
                      {
                        NEW_SCORE_MAPPINGS[
                          riskLevel ?? EnterpriseRatingSeverityDefault
                        ]
                      }
                    </RaterEnterpriseScoreTableBodyCell>
                    <RaterEnterpriseScoreTableBodyCell>
                      <RaterNotesButton
                        uuid={quoteUuid ?? ''}
                        targetName={NoteTargetName.HasEnterpriseRating}
                        targetType={NoteTargetType.Relationship}
                      />
                    </RaterEnterpriseScoreTableBodyCell>
                  </TableRow>
                  <RaterNotesRow
                    colSpan={4}
                    uuid={quoteUuid ?? ''}
                    targetName={NoteTargetName.HasEnterpriseRating}
                    targetType={NoteTargetType.Relationship}
                  />
                </Fragment>
              );
            })}
            <TableRow>
              <RaterEnterpriseScoreTableHeaderCell>
                Total Enterprise Fusion Score
              </RaterEnterpriseScoreTableHeaderCell>
              <TableCell />
              <RaterEnterpriseScoreTableHeaderCell>
                {totalScore.toFixed(2)}
              </RaterEnterpriseScoreTableHeaderCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </FormProvider>
      <Stack className="enterprise-fusion-container">
        <FormLabel>Enterprise Fusion Score Modifier</FormLabel>
        <Box className="enterprise-fusion-score">
          <NumberField
            isDisabled={!isEditable || inForcePolicy}
            name="erSelectedFusionScoreModifier"
            value={erSelectedFusionScoreModifier}
            placeholder={erRatedFusionScoreModifier?.toFixed(2)}
            asValue
            onBlur={handleUpdateEnterpriseFusionScore}
            error={hasInvalidSelectedFusionScoreModifier}
            min={
              erMinFusionScoreModifier ??
              config.defaults.enterpriseRatingMinFusionScoreModifier
            }
            max={
              erMaxFusionScoreModifier ??
              config.defaults.enterpriseRatingMaxFusionScoreModifier
            }
            hasChanged={hasErSelectedFusionScoreModifierChanged}
          />
          <FusionScoreModifierDisplay label="Min">
            {erMinFusionScoreModifier ??
              config.defaults.enterpriseRatingMinFusionScoreModifier}
          </FusionScoreModifierDisplay>
          <FusionScoreModifierDisplay label="Max">
            {erMaxFusionScoreModifier ??
              config.defaults.enterpriseRatingMaxFusionScoreModifier}
          </FusionScoreModifierDisplay>
          <RaterNotesButton
            uuid={`${uuid}`}
            openUuid={`${uuid}FS`}
            label={[ModifierNoteLabel.FusionScore]}
            targetArea={NoteTargetAreaType.Quote}
          />
        </Box>

        <RaterNotesRow
          colSpan={4}
          uuid={`${uuid}`}
          openUuid={`${uuid}FS`}
          label={[ModifierNoteLabel.FusionScore]}
          targetArea={NoteTargetAreaType.Quote}
        />
      </Stack>
    </>
  );
};

const RaterEnterpriseScoreTableHeaderCell = ({
  children,
  ...otherProps
}: ITableCellProps) => {
  return (
    <TableCell
      className="enterprise-clear-score-cell"
      sx={{ fontWeight: 'bold' }}
      {...otherProps}
    >
      {children}
    </TableCell>
  );
};

const RaterEnterpriseScoreTableBodyCell = ({
  children,
  ...otherProps
}: ITableCellProps) => {
  return (
    <TableCell className="enterprise-clear-score-cell" {...otherProps}>
      {children}
    </TableCell>
  );
};
